import React, { createContext, useContext, useEffect, useState } from 'react'

import { api, User, API_BASE_URL, GateAccessError } from '../api/api'
import { applyPalette, currentPalette, isPalette, recentlyChangedLocally } from '../utils/palette'
import { clearGateAutoRedirect, gateLogoutUrl, gateReturnAllowed, setSessionActive } from '../utils/gate'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  /** Включён ли единый вход ECMIT (флаг ECMIT_GATE_LOGIN на сервере). */
  gateEnabled: boolean
  /** Единый вход включён, но task или «Вход» не отвечают: работаем по-старому (Яндекс ID / пароль). */
  gateDown: boolean
  /** Единый вход подтвердил человека, но допуска к CRM нет: показываем «нет доступа», а не форму входа. */
  accessDenied: string | null
}

interface AuthContextType extends AuthState {
  loginWithPassword: (email: string, password: string) => Promise<boolean>
  loginWithYandex: () => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null, isAuthenticated: false, isLoading: true, error: null, gateEnabled: false, gateDown: false, accessDenied: null,
  })

  useEffect(() => {
    // Remove credentials produced by vulnerable legacy frontend versions.
    localStorage.removeItem('auth_token')
    localStorage.removeItem('authorized_user')
    let alive = true
    ;(async () => {
      let gateEnabled = false
      let gateDown = false
      try {
        const config = await api.getGateConfig()
        gateDown = config.enabled && config.available === false
        gateEnabled = config.enabled && !gateDown  // лежит task или «Вход»: не уводим на единый вход, показываем прежний
      } catch { /* старый сервер: единого входа нет */ }
      try {
        const user = await api.getCurrentUser()
        if (!alive) return
        clearGateAutoRedirect()
        setSessionActive(true)
        setAuthState({ user, isAuthenticated: true, isLoading: false, error: null, gateEnabled, gateDown, accessDenied: null })
      } catch (error) {
        if (!alive) return
        const accessDenied = error instanceof GateAccessError ? error.message : null
        setAuthState({ user: null, isAuthenticated: false, isLoading: false, error: null, gateEnabled, gateDown, accessDenied })
      }
    })()
    return () => { alive = false }
  }, [])

  // Палитра хранится у человека в «Входе» и меняется на лендинге в любой момент, а сессия CRM живёт до 30 дней,
  // поэтому подтягиваем её не только при входе: раз в минуту, когда вкладка видна, и сразу при возврате на неё.
  // Без выбора в «Входе» — мята, как по всей системе ECMIT. Если источник недоступен, локальный выбор не трогаем.
  const { isAuthenticated, gateEnabled } = authState
  useEffect(() => {
    if (!isAuthenticated || !gateEnabled) return
    let stopped = false
    const sync = () => {
      if (document.visibilityState === 'hidden' || recentlyChangedLocally()) return
      api.getGatePalette()
        .then((result) => {
          if (stopped || recentlyChangedLocally() || !result.source) return
          const next = isPalette(result.palette) ? result.palette : 'mint'
          if (next !== currentPalette()) applyPalette(next)
        })
        .catch(() => undefined)
    }
    sync()
    const timer = window.setInterval(sync, 60_000)
    const onVisible = () => { if (document.visibilityState === 'visible') sync() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', sync)
    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', sync)
    }
  }, [isAuthenticated, gateEnabled])

  const loginWithPassword = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await api.loginViaEmail(email, password)
      const user = response.user || await api.getCurrentUser()
      setAuthState((current) => ({ ...current, user, isAuthenticated: true, isLoading: false, error: null, accessDenied: null }))
      return true
    } catch (error: any) {
      setAuthState((current) => ({ ...current, isLoading: false, error: error?.message || 'Не удалось войти' }))
      return false
    }
  }

  const loginWithYandex = () => {
    const startUrl = `${API_BASE_URL}/auth/yandex/start`

    // Older installed PWA workers treated every navigation as an SPA route
    // and returned index.html for /api/auth/yandex/start. Remove those workers
    // before leaving the app; the corrected worker is registered on next load.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        .finally(() => window.location.assign(startUrl))
      return
    }

    window.location.assign(startUrl)
  }

  const logout = async () => {
    setSessionActive(false)
    await api.logout().catch(() => undefined)
    setAuthState((current) => ({ ...current, user: null, isAuthenticated: false, isLoading: false, error: null, accessDenied: null }))
    // Выход единого входа гасит сессию везде (другие службы тоже), поэтому уходим на него обычным переходом.
    if (authState.gateEnabled && gateReturnAllowed()) {
      window.location.assign(gateLogoutUrl(`${window.location.origin}/login`))
    }
  }

  return <AuthContext.Provider value={{ ...authState, loginWithPassword, loginWithYandex, logout }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
